import type { QRCodeProps, ModalProps } from 'antd';
import * as htmlToImage from 'html-to-image';
import { message, QRCode } from 'antd';
import GroupMembersIcon from '@/assets/images/common/svg/group-members-icon.svg?react';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { ChatCategory } from '@/contexts/chat/scripts/hooks/useChats/types';
import styles from './index.module.scss';
import CopyIcon from './images/copy-icon.svg?react';
import MembersEllipsisIcon from './images/members-ellipsis.svg?react';

interface ShareQrCodeProps extends QRCodeProps {
  qrCodeName?: string;
  size?: number;
  // errorLevel?: 'L' | 'M' | 'Q' | 'H';
}

interface QrCodeModalProps extends ModalProps {
  title?: string;
}

interface ShareQrCodeModalProps {
  headerAvatar: string;
  headerName: string;
  shortName?: string;
  members?: ChatGroupMember[];
  qrCodeProps: ShareQrCodeProps;
  modalProps: QrCodeModalProps;
}

export const ShareQrCodeModal = ({
  shortName,
  headerAvatar,
  headerName,
  members,
  qrCodeProps,
  modalProps,
}: ShareQrCodeModalProps) => {
  const { qrCodeName = 'QRCode', size = 100, ...qrProps } = qrCodeProps;

  const isGroup = useMemo(() => members && members?.length > 0, [members]);

  const qrCodeUrl = useMemo(() => {
    if (qrProps.value) {
      const url_ = new URL(qrProps.value);
      return url_.host.slice(0, 30) + '/...' + shortName;
    }
    return '';
  }, [qrProps.value, shortName]);

  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeContainerRef = useRef<HTMLDivElement>(null);

  const download = useCallback((url: string, fileName: string) => {
    const a = document.createElement('a');
    a.download = fileName;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const downloadCanvasQRCode = useCallback(
    (cb?: any) => {
      const node = qrCodeContainerRef.current as HTMLElement;
      const logoBar = node.querySelector<HTMLDivElement>('#logoBar');
      if (logoBar) {
        // logoBar.style.display = 'flex';
      }
      htmlToImage
        .toBlob(node, {
          cacheBust: true,
          fetchRequestInit: { mode: 'cors', credentials: 'omit' },
          backgroundColor: '#fff',
        })
        .then((blob) => {
          if (logoBar) {
            // logoBar.style.display = 'none';
          }
          if (cb) {
            cb(blob);
          } else {
            const url = URL.createObjectURL(blob!);
            download(url, `${qrCodeName}.png`);
            const timer = setTimeout(() => {
              URL.revokeObjectURL(url);
              clearTimeout(timer);
            }, 1000);
          }
        })
        .catch(() => {
          if (logoBar) {
            logoBar.style.display = 'none';
          }
        });
    },
    [download, qrCodeName]
  );

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(qrProps.value as string);
    message.success('Copied');
  }, [qrProps.value]);

  const copyQrCode = useCallback(() => {
    downloadCanvasQRCode((blob: Blob) => {
      navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      message.success('Copied');
    });
  }, [downloadCanvasQRCode]);

  return (
    <Modal
      className={styles.shareQrCodeModal}
      title={modalProps.title}
      {...modalProps}
      footer={null}
      width={410}
      centered
    >
      <div className={styles.shareQrCode} ref={qrCodeRef}>
        <div
          className={cls(
            styles.qrCodeContainerWrapper,
            isGroup && styles.isGroupWrapper
          )}
          ref={qrCodeContainerRef}
        >
          <Flex
            gap={16}
            vertical
            align="center"
            className={cls(styles.qrCodeContainer, isGroup && styles.isGroupContainer)}
          >
            <div className={cls(styles.chatHeader, isGroup && styles.isGroup)}>
              <MyAvatar
                category={isGroup ? ChatCategory.GROUP : ChatCategory.SINGLE}
                src={headerAvatar}
                className={styles.chatAvatar}
                showProfileClick={false}
              />
              <div className={styles.chatHeaderName}>
                {isGroup && <GroupMembersIcon />}
                <div className={styles.chatHeaderNameText}>{headerName}</div>
              </div>
              {isGroup && (
                <div className={styles.chatHeaderMembers}>
                  {members
                    ?.slice(0, 3)
                    .map((member) => (
                      <MyAvatar
                        key={member.user_id}
                        src={member.avatar}
                        size="small"
                        className={styles.avatar}
                        info={member}
                      />
                    ))}
                  {members && members?.length > 3 && (
                    <MembersEllipsisIcon className={styles.avatar} />
                  )}
                </div>
              )}
            </div>
            <QRCode size={size} type="canvas" iconSize={size / 4} {...qrProps} />
            <div className={styles.qrCodeText}>@{shortName}</div>
            <div className={styles.logoBar} id="logoBar" style={{ display: 'none' }}>
              <img src="/favicon.png" alt="logo" />
              <div>HaloChat</div>
            </div>
          </Flex>
        </div>
        <Flex gap={20} vertical style={{ width: '100%', padding: '0 16px' }}>
          <div className={styles.linkToProfile}>
            <div>
              <div className={styles.linkToProfileText}>Link to profile</div>
              <div className={styles.linkToProfileValue}>{qrCodeUrl}</div>
            </div>
            <CopyIcon className="pointer" onClick={copyLink} />
          </div>
          <Flex gap={16} justify="space-between" className={styles.buttonContainer}>
            <Button
              size="large"
              color="primary"
              variant="outlined"
              className={styles.myButton}
              onClick={() => downloadCanvasQRCode()}
            >
              Save Image
            </Button>
            <Button
              size="large"
              type="primary"
              className={styles.myButton}
              onClick={copyQrCode}
            >
              Copy
            </Button>
          </Flex>
        </Flex>
      </div>
    </Modal>
  );
};
